import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    convertIdsToDisplay,
    convertDisplayToIds,
    migrateOldVariableFormat,
    containsVariables,
    extractVariableIds
} from '../../../../utils/automation/variableUtils';

/**
 * useVariables Hook
 * 
 * Centralized hook for variable management across all ReplayPage components.
 * Provides:
 * - Variables data from Redux store
 * - Memoized conversion functions
 * - Helper utilities for variable operations
 * 
 * @returns {Object} - Object containing variables data and utility functions
 * 
 * @example
 * const { variablesList, variablesListDescriptions, convertToDisplay, convertToIds } = useVariables();
 * const displayText = convertToDisplay(storedValue);
 */
export const useVariables = () => {
    // Get variables from Redux store
    const { variablesList = [], variablesListDescriptions = {} } = useSelector(
        (state) => state.replay
    );

    // Memoize conversion functions to prevent unnecessary re-renders
    const convertToDisplay = useCallback(
        (storedValue) => {
            return convertIdsToDisplay(storedValue, variablesList, variablesListDescriptions);
        },
        [variablesList, variablesListDescriptions]
    );

    const convertToIds = useCallback(
        (displayValue) => {
            return convertDisplayToIds(displayValue, variablesList, variablesListDescriptions);
        },
        [variablesList, variablesListDescriptions]
    );

    const migrateOldFormat = useCallback(
        (value) => {
            return migrateOldVariableFormat(value, variablesList, variablesListDescriptions);
        },
        [variablesList, variablesListDescriptions]
    );

    // Memoize helper functions
    const hasVariables = useCallback(
        (value) => containsVariables(value),
        []
    );

    const getVariableIds = useCallback(
        (value) => extractVariableIds(value),
        []
    );

    // Memoize the entire return object to prevent unnecessary re-renders
    return useMemo(
        () => ({
            // Variables data
            variablesList,
            variablesListDescriptions,
            
            // Conversion functions
            convertToDisplay,
            convertToIds,
            migrateOldFormat,
            
            // Helper functions
            hasVariables,
            getVariableIds,
        }),
        [
            variablesList,
            variablesListDescriptions,
            convertToDisplay,
            convertToIds,
            migrateOldFormat,
            hasVariables,
            getVariableIds,
        ]
    );
};

export default useVariables;

